<template>
  <v-form ref="form" @input="onChange">
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Dodaj artikle na cjenik</h3>
      </v-col>
    </v-row>

    <div
      v-for="(cat, catIdx) in categories"
      :key="`cat-${catIdx}`"
      class="category-block mb-5"
    >
      <!-- Category name row -->
      <div class="d-flex align-center mb-2">
        <v-text-field
          v-model="cat.name"
          label="Naziv kategorije"
          dense
          outlined
          :rules="[rules.req]"
          hide-details="auto"
          class="flex-grow-1"
        />
        <v-btn
          v-if="categories.length > 1"
          icon
          small
          class="ml-2 flex-shrink-0"
          @click="removeCategory(catIdx)"
        >
          <v-icon color="grey darken-1" size="20">mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>

      <!-- Items -->
      <v-card
        v-for="(item, itemIdx) in cat.items"
        :key="`item-${catIdx}-${itemIdx}`"
        outlined
        class="mb-2 item-card"
        elevation="0"
      >
        <!-- Floating delete button -->
        <v-btn
          v-if="cat.items.length > 1 || categories.length > 1"
          icon
          x-small
          class="item-delete-btn"
          @click="removeItem(catIdx, itemIdx)"
        >
          <v-icon size="16" color="grey darken-1">mdi-close</v-icon>
        </v-btn>

        <v-row dense class="pa-3 pt-4">
          <!-- Naziv artikla — full width on mobile, 4/12 on desktop -->
          <v-col cols="12" sm="12" md="4">
            <v-text-field
              v-model="item.name"
              label="Naziv artikla"
              dense
              outlined
              :rules="[rules.req]"
              hide-details="auto"
            />
          </v-col>

          <!-- Cijena -->
          <v-col cols="6" sm="4" md="2">
            <v-text-field
              v-model.number="item.price"
              label="Cijena (EUR)"
              type="number"
              min="0.01"
              step="0.01"
              dense
              outlined
              :rules="[rules.req, rules.positivePrice]"
              hide-details="auto"
            />
          </v-col>

          <!-- JMJ -->
          <v-col cols="6" sm="4" md="2">
            <v-select
              v-model="item.unit"
              :items="units"
              item-text="name"
              item-value="value"
              label="JMJ"
              dense
              outlined
              :rules="[rules.req]"
              hide-details="auto"
            />
          </v-col>

          <!-- PDV — prikaži samo ako je firma u sustavu PDV-a -->
          <v-col v-if="isTaxed" cols="6" sm="4" md="2">
            <v-select
              v-model="item.taxRate"
              :items="taxRates"
              item-text="name"
              item-value="rate"
              label="PDV"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>

          <!-- KPD šifra -->
          <v-col cols="6" sm="4" md="2">
            <v-text-field
              v-model="item.kpdcode"
              label="KPD šifra (opt.)"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-btn text small color="primary" class="mt-1 pl-0" @click="addItem(catIdx)">
        <v-icon left small>mdi-plus</v-icon>
        Dodaj artikl
      </v-btn>
    </div>

    <v-divider class="my-4" />

    <v-btn text color="primary" class="pl-0" @click="addCategory">
      <v-icon left>mdi-folder-plus-outline</v-icon>
      Dodaj novu kategoriju
    </v-btn>
  </v-form>
</template>

<script>
import rules from '@/plugins/rules'

const TAX_RATES = [
  { name: 'PDV 0%', rate: 0 },
  { name: 'PDV 5%', rate: 500 },
  { name: 'PDV 13%', rate: 1300 },
  { name: 'PDV 25%', rate: 2500 }
]

export default {
  name: 'StepPriceList',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    companyData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      categories: [],
      units: [
        { name: 'Komad', value: 'komad' },
        { name: 'Kilogram', value: 'kilogram' },
        { name: 'Litra', value: 'litra' },
        { name: 'Sat', value: 'sat' },
        { name: 'm²', value: 'm2' },
        { name: 'm', value: 'm' }
      ],
      taxRates: TAX_RATES,
      rules: {
        req: rules.req(),
        positivePrice: v => (v > 0) || 'Cijena mora biti veća od 0'
      }
    }
  },
  computed: {
    isTaxed () {
      return !!this.companyData.is_taxed
    },
    defaultTaxRate () {
      return this.isTaxed ? 2500 : 0
    }
  },
  watch: {
    categories: {
      handler () {
        this.$emit('input', { categories: this.categories })
      },
      deep: true
    },
    isTaxed (val) {
      const rate = val ? 2500 : 0
      this.categories.forEach(cat => {
        cat.items.forEach(item => { item.taxRate = rate })
      })
    }
  },
  created () {
    this.categories = [this.makeCategory()]
  },
  mounted () {
    this.$emit('input', { categories: this.categories })
  },
  methods: {
    makeItem () {
      return { name: 'Artikl', price: 1.00, unit: 'komad', kpdcode: '', taxRate: this.defaultTaxRate }
    },
    makeCategory () {
      return { name: 'Naziv kategorije', items: [this.makeItem()] }
    },
    addCategory () {
      this.categories.push(this.makeCategory())
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation() })
    },
    removeCategory (catIdx) {
      this.categories.splice(catIdx, 1)
    },
    addItem (catIdx) {
      this.categories[catIdx].items.push(this.makeItem())
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation() })
    },
    removeItem (catIdx, itemIdx) {
      this.categories[catIdx].items.splice(itemIdx, 1)
      if (this.categories[catIdx].items.length === 0) {
        this.categories.splice(catIdx, 1)
        if (this.categories.length === 0) this.categories.push(this.makeCategory())
      }
    },
    validate () {
      return this.$refs.form.validate()
    },
    getData () {
      return { categories: this.categories }
    },
    focusItemName () {},
    onChange () {
      this.$emit('change')
    }
  }
}
</script>

<style scoped>
h3 {
  color: #1577da;
}

.category-block {
  border-left: 3px solid rgba(57, 137, 201, 0.3);
  padding-left: 12px;
}

.item-card {
  position: relative;
  background: rgba(255, 255, 255, 0.5) !important;
  border-color: rgba(57, 137, 201, 0.15) !important;
  border-radius: 8px !important;
}

.item-delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
}
</style>
